/**
 * Moteur de synchronisation cross-device + partage par compte.
 */

import { authFetch, isLoggedIn } from '../utils/auth.js';
import { db } from '../db/index.js';
import { getAllStores } from '../db/stores.js';
import { getAislesByStore } from '../db/aisles.js';
import { getListItems, saveArticleTemplate } from '../db/articles.js';

const SYNC_DONE_KEY = 'grocewise-initial-sync-done';

function isSyncDone() {
  return localStorage.getItem(SYNC_DONE_KEY) === 'true';
}

function markSyncDone() {
  localStorage.setItem(SYNC_DONE_KEY, 'true');
}

/**
 * Sync initiale : appelée après le login.
 */
export async function initialSync() {
  if (!isLoggedIn()) return;

  if (!isSyncDone()) {
    await uploadLocalData();
    markSyncDone();
  }

  await pullData();
}

/**
 * Upload toutes les données locales vers le serveur (premier login).
 */
async function uploadLocalData() {
  const stores = await getAllStores();
  if (stores.length === 0) return;

  const storesData = [];
  for (const store of stores) {
    const aisles = await getAislesByStore(store.id);
    const items = await getListItems(store.id);
    storesData.push({
      name: store.name,
      order: store.order ?? 0,
      aisles: aisles.map((a) => ({ name: a.name, order: a.order })),
      items: items.map((i) => ({
        name: i.name,
        aisle_name: aisles.find((a) => a.id === i.aisleId)?.name ?? null,
        quantity: i.quantity,
        checked: i.checked,
      })),
    });
  }

  const result = await authFetch('/user/sync', {
    method: 'POST',
    body: JSON.stringify({ stores: storesData }),
  });

  await applyServerData(result);
}

/**
 * Pull les données du serveur et les applique localement.
 */
export async function pullData() {
  if (!isLoggedIn()) return;
  const data = await authFetch('/user/data');
  await applyServerData(data);
}

/**
 * Applique les données serveur dans l'IndexedDB local.
 */
async function applyServerData(data) {
  if (!data?.stores) return;

  const seenStoreServerIds = new Set();

  for (const serverStore of data.stores) {
    seenStoreServerIds.add(serverStore.id);
    let localStore = await db.stores.filter((s) => s.serverId === serverStore.id).first();

    if (!localStore) {
      const localId = await db.stores.add({
        name: serverStore.name,
        order: serverStore.order,
        createdAt: new Date(),
        serverId: serverStore.id,
        role: serverStore.role || 'owner',
        shareCode: serverStore.share_code || null,
      });
      localStore = { id: localId, serverId: serverStore.id };
    } else {
      await db.stores.update(localStore.id, {
        name: serverStore.name,
        order: serverStore.order,
        role: serverStore.role || 'owner',
        shareCode: serverStore.share_code || null,
      });
    }

    const storeId = localStore.id;

    for (const serverAisle of (serverStore.aisles || [])) {
      let localAisle = await db.aisles
        .where('storeId').equals(storeId)
        .filter((a) => a.serverId === serverAisle.id)
        .first();

      if (!localAisle) {
        await db.aisles.add({
          storeId,
          name: serverAisle.name,
          order: serverAisle.order,
          createdAt: new Date(),
          serverId: serverAisle.id,
        });
      } else {
        await db.aisles.update(localAisle.id, {
          name: serverAisle.name,
          order: serverAisle.order,
        });
      }
    }

    const seenItemServerIds = new Set();

    for (const serverItem of (serverStore.items || [])) {
      seenItemServerIds.add(serverItem.id);

      const existingItem = await db.listItems
        .where('storeId').equals(storeId)
        .filter((i) => i.serverId === serverItem.id)
        .first();

      const aisleId = serverItem.aisle_id
        ? (await db.aisles.where('storeId').equals(storeId)
            .filter((a) => a.serverId === serverItem.aisle_id).first())?.id ?? null
        : null;

      if (!existingItem) {
        await db.listItems.add({
          storeId,
          name: serverItem.name,
          aisleId,
          quantity: serverItem.quantity,
          checked: !!serverItem.checked,
          addedAt: new Date(),
          serverId: serverItem.id,
        });
      } else {
        await db.listItems.update(existingItem.id, {
          name: serverItem.name,
          aisleId,
          quantity: serverItem.quantity,
          checked: !!serverItem.checked,
        });
      }

      // Sauvegarder dans l'historique pour l'autocomplétion future
      try {
        await saveArticleTemplate(storeId, serverItem.name, aisleId);
      } catch { /* template déjà existant, ignorer */ }
    }

    // Supprimer les items locaux qui n'existent plus sur le serveur
    const localItems = await db.listItems.where('storeId').equals(storeId).toArray();
    for (const localItem of localItems) {
      if (localItem.serverId && !seenItemServerIds.has(localItem.serverId)) {
        await db.listItems.delete(localItem.id);
      }
    }

    // Supprimer les rayons locaux qui n'existent plus sur le serveur
    const seenAisleServerIds = new Set((serverStore.aisles || []).map((a) => a.id));
    const localAisles = await db.aisles.where('storeId').equals(storeId).toArray();
    for (const localAisle of localAisles) {
      if (localAisle.serverId && !seenAisleServerIds.has(localAisle.serverId)) {
        await db.aisles.delete(localAisle.id);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Push helpers — appelés après chaque action locale quand connecté
// ---------------------------------------------------------------------------

export async function pushCreateStore(name, order) {
  if (!isLoggedIn()) return null;
  try {
    return await authFetch('/user/stores', {
      method: 'POST',
      body: JSON.stringify({ name, order }),
    });
  } catch { return null; }
}

export async function pushUpdateStore(serverId, name, order) {
  if (!isLoggedIn() || !serverId) return;
  try {
    await authFetch(`/user/stores/${serverId}`, {
      method: 'PUT',
      body: JSON.stringify({ name, order }),
    });
  } catch { /* silent */ }
}

export async function pushDeleteStore(serverId) {
  if (!isLoggedIn() || !serverId) return;
  try {
    await authFetch(`/user/stores/${serverId}`, { method: 'DELETE' });
  } catch { /* silent */ }
}

export async function pushCreateAisle(storeServerId, name, order) {
  if (!isLoggedIn() || !storeServerId) return null;
  try {
    return await authFetch(`/user/stores/${storeServerId}/aisles`, {
      method: 'POST',
      body: JSON.stringify({ name, order }),
    });
  } catch { return null; }
}

export async function pushUpdateAisle(serverId, name, order) {
  if (!isLoggedIn() || !serverId) return;
  try {
    await authFetch(`/user/aisles/${serverId}`, {
      method: 'PUT',
      body: JSON.stringify({ name, order }),
    });
  } catch { /* silent */ }
}

export async function pushDeleteAisle(serverId) {
  if (!isLoggedIn() || !serverId) return;
  try {
    await authFetch(`/user/aisles/${serverId}`, { method: 'DELETE' });
  } catch { /* silent */ }
}

export async function pushCreateItem(storeServerId, name, aisleName, quantity, checked) {
  if (!isLoggedIn() || !storeServerId) return null;
  try {
    return await authFetch(`/user/stores/${storeServerId}/items`, {
      method: 'POST',
      body: JSON.stringify({ name, aisle_name: aisleName, quantity, checked }),
    });
  } catch { return null; }
}

export async function pushUpdateItem(serverId, name, aisleName, quantity, checked) {
  if (!isLoggedIn() || !serverId) return;
  try {
    await authFetch(`/user/items/${serverId}`, {
      method: 'PUT',
      body: JSON.stringify({ name, aisle_name: aisleName, quantity, checked }),
    });
  } catch { /* silent */ }
}

export async function pushDeleteItem(serverId) {
  if (!isLoggedIn() || !serverId) return;
  try {
    await authFetch(`/user/items/${serverId}`, { method: 'DELETE' });
  } catch { /* silent */ }
}

// ---------------------------------------------------------------------------
// Partage
// ---------------------------------------------------------------------------

export async function shareStore(storeServerId, role = 'editor') {
  return authFetch(`/user/stores/${storeServerId}/share`, {
    method: 'POST',
    body: JSON.stringify({ role }),
  });
}

export async function unshareStore(storeServerId) {
  return authFetch(`/user/stores/${storeServerId}/share`, { method: 'DELETE' });
}

export async function joinStore(code) {
  return authFetch(`/user/join/${code}`, { method: 'POST' });
}

export async function getStoreMembers(storeServerId) {
  return authFetch(`/user/stores/${storeServerId}/members`);
}

export async function removeMember(storeServerId, userId) {
  return authFetch(`/user/stores/${storeServerId}/members/${userId}`, { method: 'DELETE' });
}
